import i18n, {getI18nDefaultLanguage} from '@/lang/lang'

/**
 * Vue Router middleware to handle maintenance mode.
 * If maintenance mode is active, this middleware will redirect all non-excluded routes
 * to a dedicated maintenance page.
 *
 * @param {object} to - The target Route Object being navigated to.
 * @param {object} from - The current Route Object being navigated away from.
 * @param {Function} next - The function to resolve the hook.
 * @param {object} injection - Injected properties, expected to contain the Vue instance.
 */
export default async (to, from, next, injection) => {
  const vue = injection
  const inMaintenanceMode = vue?.AppConfig?.Marketplace?.MaintenanceMode ?? false

  /**
   * Helper function to perform the redirection.
   * It uses the 'next' callback otherwise falls back to the router instance.
   * @param {object} route - The route object to redirect to.
   */
  const replaceRoute = (route) => next ? next(route) : vue?.$router?.replace(route)

  // Handle maintenance mode access override ('mma') flag from query params and session storage.
  if (to?.query?.mma === 'skip') vue.$sessionStorage.set('mma', true)
  if (!inMaintenanceMode) vue.$sessionStorage.remove('mma')
  const hasSkipAccess = to?.query?.mma === 'skip' || vue.$sessionStorage.get('mma') === true

  // Define a list of route names that should be accessible even when in maintenance mode.
  const excludedRoutes = ['MaintenanceMode', 'MarketplaceAccountConnected', 'VivaMarketplaceAccountConnected']
  const isExcludedRoute = excludedRoutes.includes(to?.name)

  const shouldRedirectToMaintenance = inMaintenanceMode && !isExcludedRoute && !hasSkipAccess
  const shouldRedirectFromMaintenance = !inMaintenanceMode && to?.name === 'MaintenanceMode'

  if (shouldRedirectToMaintenance || shouldRedirectFromMaintenance) {
    const routeLang = to?.params?.lang
    const langExists = i18n.messages.hasOwnProperty(routeLang) && i18n.languages?.find(l => l.locale === routeLang)?.active
    const lang = langExists ? routeLang : getI18nDefaultLanguage()?.locale

    const targetRouteName = shouldRedirectToMaintenance ? 'MaintenanceMode' : 'Home'

    // If in maintenance mode, redirect to the maintenance page (unless excluded or skipping).
    // If not in maintenance mode but on the maintenance page, redirect to the home page.
    replaceRoute({
      replace: true,
      name   : targetRouteName,
      params : {lang: lang},
      query  : to?.query || {}
    })
  }
}
